import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import {
  apiDeleteTransactionFile,
  apiGetTransactionFiles,
  apiUploadTransactionFile,
} from "@lib/api/transaction";
import {
  TransactionFileUpload,
  type FileUploadProps,
} from "@lib/components/MiniComponents/FileUpload";
import { ProviderTransactionFileConfig } from "@lib/constants/Provider";
import {
  TransactionAttachmentFile,
  UseQueryProps,
  TransactionType,
  TransactionAttachmentStatus,
  UseMutationProps,
  ApiParams,
  TransactionTypeLog,
  TransactionTypeLogType,
} from "@lib/types";
import { RcFile } from "antd/lib/upload";
import { useQuery } from "@tanstack/react-query";

type Props = {
  transaction?: TransactionType;
  transactionUpdateMutation: UseMutationProps<Partial<TransactionType>>;
  paymentHistoryTransactionsQuery?: UseQueryProps<TransactionType>;
  fileUploadProps?: Partial<FileUploadProps>;
  createTransactionLog: (
    data: Pick<TransactionTypeLog, "transactionId" | "comment" | "type">,
  ) => void;
};

export default function FileUpload(props: Props) {
  const {
    transaction,
    paymentHistoryTransactionsQuery,
    transactionUpdateMutation,
    fileUploadProps,
    createTransactionLog,
  } = props;
  const [uploadedFiles, setUploadedFiles] = useState<
    TransactionAttachmentFile[]
  >([]);

  const { isPending: isUpdating } = transactionUpdateMutation;
  const params: ApiParams = {
    filter: [`transactionId||$eq||${transaction?.id}`],
    join: ["manager"],
    sort: "createdAt,DESC",
  };

  const { data: attachments, isFetching: isFetchingAttachments } = useQuery({
    queryKey: ["attachments", JSON.stringify(params)],
    queryFn: () => apiGetTransactionFiles(params),
    enabled: !!transaction?.id && !!params,
  });
  const apiData = attachments?.data?.data;

  useEffect(() => {
    // Only update if we don't have any uploading files
    setUploadedFiles((prevFiles) => {
      const hasUploadingFiles = prevFiles.some(
        (file) => file.status === TransactionAttachmentStatus.Uploading,
      );

      if (!hasUploadingFiles) {
        return (apiData || []).map(
          (attachment) =>
            ({
              uid: attachment.id,
              lastModified: dayjs(attachment.createdAt).valueOf(),
              lastModifiedDate: dayjs(attachment.updatedAt).toDate(),
              name: attachment.originalFileName,
              size: attachment?.size || null,
              type: attachment.mimeType,
              webkitRelativePath: "",
              data: attachment,
              status: TransactionAttachmentStatus.Done,
              manager: attachment.manager,
            }) as TransactionAttachmentFile,
        );
      }
      return prevFiles;
    });
  }, [apiData, isFetchingAttachments]);

  const handleFileUpload = async (
    file: RcFile,
    onProgress: (progress: number) => void,
  ) => {
    if (!transaction?.id) {
      throw new Error("Transaction ID is required");
    }

    try {
      await apiUploadTransactionFile(transaction.id, file, onProgress);

      paymentHistoryTransactionsQuery?.refetch();
    } catch (error) {
      console.error("File upload error:", error);
      createTransactionLog({
        comment: `File upload failed: ${file.name}`,
        transactionId: transaction?.id,
        type: TransactionTypeLogType.Failed,
      });
      throw error;
    }
  };

  const handleFileRemove = async (file: TransactionAttachmentFile) => {
    const fileId = file.uid;
    if (file?.data?.id) {
      // delete file from server
      try {
        await apiDeleteTransactionFile(file.data.id);

        createTransactionLog({
          comment: `File deleted: ${file.name}`,
          transactionId: transaction?.id,
          type: TransactionTypeLogType.Deleted,
        });

        paymentHistoryTransactionsQuery?.refetch();
        setUploadedFiles((prev) => prev.filter((f) => f.uid !== fileId));
      } catch (error) {
        createTransactionLog({
          comment: `File deletion failed: ${file.name}`,
          transactionId: transaction?.id,
          type: TransactionTypeLogType.Failed,
        });
      }
    }
  };

  const handleFilesChange = useCallback(
    (files: TransactionAttachmentFile[]) => {
      setUploadedFiles(files);
    },
    [],
  );

  return (
    <div style={{ marginBottom: 8, padding: 10 }}>
      <TransactionFileUpload
        onUpload={handleFileUpload}
        onRemove={handleFileRemove}
        onFilesChange={handleFilesChange}
        disabled={isUpdating}
        uploadedFiles={uploadedFiles}
        acceptedTypes={ProviderTransactionFileConfig.types}
        maxSize={ProviderTransactionFileConfig.maxSize}
        {...fileUploadProps}
      />
    </div>
  );
}
